import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { formatPhone } from '../utils/formatPhone';

const STATUS_COLORS = {
  open:               '#6366f1',
  in_progress:        '#f97316',
  waiting_on_learner: '#facc15',
  resolved:           '#22c55e',
  closed:             '#475569',
};

const PRIORITY_COLORS = {
  low:    '#64748b',
  medium: '#3b82f6',
  high:   '#f97316',
  urgent: '#ef4444',
};

const STATUS_LABELS = {
  open:               'Open',
  in_progress:        'In Progress',
  waiting_on_learner: 'Waiting',
  resolved:           'Resolved',
  closed:             'Closed',
};

const ALL_STATUSES = ['open', 'in_progress', 'waiting_on_learner', 'resolved', 'closed'];
const ALL_PRIORITIES = ['low', 'medium', 'high', 'urgent'];

// ── Ticket Detail Drawer ──────────────────────────────────────────────────────
function TicketDrawer({ ticket, onClose, onUpdate }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [resolveAnswer, setResolveAnswer] = useState('');
  const [showResolve, setShowResolve] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [msgLoading, setMsgLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const isAdmin = user?.role === 'orgadmin' || user?.role === 'superadmin';

  useEffect(() => {
    loadMessages();
    if (isAdmin) loadUsers();
  }, [ticket._id]); // eslint-disable-line

  async function loadMessages() {
    setMsgLoading(true);
    try {
      const { data } = await api.get(`/api/tickets/${ticket._id}`);
      setMessages(data.messages);
    } catch {
      setMessages([]);
    }
    setMsgLoading(false);
  }

  async function loadUsers() {
    try {
      const { data } = await api.get('/api/org/users');
      setUsers(data.filter(u => u.isActive));
    } catch {
      setUsers([]);
    }
  }

  async function postReply() {
    if (!reply.trim()) return;
    setLoading(true);
    try {
      await api.post(`/api/tickets/${ticket._id}/messages`, { content: reply.trim(), isInternal: false });
      setReply('');
      loadMessages();
    } catch (err) {
      alert(err.message);
    }
    setLoading(false);
  }

  async function postNote() {
    if (!note.trim()) return;
    setLoading(true);
    try {
      await api.post(`/api/tickets/${ticket._id}/note`, { content: note.trim() });
      setNote('');
      setShowNote(false);
      loadMessages();
    } catch (err) {
      alert(err.message);
    }
    setLoading(false);
  }

  async function resolveTicket() {
    if (!resolveAnswer.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/api/tickets/${ticket._id}/resolve`, { answer: resolveAnswer.trim() });
      onUpdate({ ...ticket, status: 'resolved' });
      if (data.waError) alert('Resolved, but WhatsApp send failed: ' + data.waError);
      else if (data.messageSent) alert('Resolved and answer sent to learner via WhatsApp.');
      else alert('Resolved. WhatsApp client offline — answer saved but not delivered yet.');
      setShowResolve(false);
      loadMessages();
    } catch (err) {
      alert(err.message);
    }
    setLoading(false);
  }

  async function updateStatus(status) {
    try {
      const { data } = await api.patch(`/api/tickets/${ticket._id}/status`, { status });
      onUpdate(data.ticket);
    } catch (err) {
      alert(err.message);
    }
  }

  async function assignTicket(userId) {
    try {
      const { data } = await api.patch(`/api/tickets/${ticket._id}/assign`, { assignedTo: userId || null });
      onUpdate(data.ticket);
    } catch (err) {
      alert(err.message);
    }
  }

  async function updatePriority(priority) {
    try {
      const { data } = await api.patch(`/api/tickets/${ticket._id}/priority`, { priority });
      onUpdate(data.ticket);
    } catch (err) {
      alert(err.message);
    }
  }

  const isResolved = ticket.status === 'resolved' || ticket.status === 'closed';

  return (
    <div style={s.drawerOverlay} onClick={onClose}>
      <div style={s.drawer} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={s.drawerHeader}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ ...s.badge, background: PRIORITY_COLORS[ticket.priority] + '22', color: PRIORITY_COLORS[ticket.priority] }}>
                {ticket.priority?.toUpperCase()}
              </span>
              <span style={{ ...s.badge, background: STATUS_COLORS[ticket.status] + '22', color: STATUS_COLORS[ticket.status] }}>
                {STATUS_LABELS[ticket.status]}
              </span>
              <span style={{ fontSize: 12, color: '#64748b' }}>#{ticket.ticketNumber}</span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginTop: 6 }}>{ticket.subject}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {ticket.learnerName && <span>{ticket.learnerName} &middot; </span>}
              {formatPhone(ticket.phone)} &middot; {new Date(ticket.createdAt).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} style={s.closeBtn}>x</button>
        </div>

        {/* Controls */}
        <div style={s.controlRow}>
          <div style={{ flex: 1 }}>
            <label style={s.controlLabel}>Status</label>
            <select
              value={ticket.status}
              onChange={e => updateStatus(e.target.value)}
              style={s.select}
              disabled={isResolved}
            >
              {ALL_STATUSES.map(st => (
                <option key={st} value={st}>{STATUS_LABELS[st]}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={s.controlLabel}>Priority</label>
            <select
              value={ticket.priority}
              onChange={e => updatePriority(e.target.value)}
              style={s.select}
            >
              {ALL_PRIORITIES.map(p => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>
          {isAdmin && (
            <div style={{ flex: 1 }}>
              <label style={s.controlLabel}>Assigned To</label>
              <select
                value={ticket.assignedTo?._id || ticket.assignedTo || ''}
                onChange={e => assignTicket(e.target.value)}
                style={s.select}
              >
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u._id} value={u._id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Message Thread */}
        <div style={s.messageThread}>
          {msgLoading ? (
            <div style={{ color: '#64748b', padding: 20, textAlign: 'center' }}>Loading...</div>
          ) : messages.length === 0 ? (
            <div style={{ color: '#64748b', padding: 20, textAlign: 'center' }}>No messages yet</div>
          ) : (
            messages.map(msg => (
              <div key={msg._id} style={{
                ...s.messageItem,
                ...(msg.isInternal ? s.internalNote : {}),
                alignSelf: msg.authorRole === 'human' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: msg.authorRole === 'bot' ? '#6366f1' : msg.isInternal ? '#facc15' : '#22c55e' }}>
                    {msg.authorRole === 'bot' ? 'Support Bot' : msg.isInternal ? 'Internal Note' : (msg.authorName || 'Support Agent')}
                  </span>
                  <span style={{ fontSize: 10, color: '#475569' }}>
                    {new Date(msg.createdAt).toLocaleTimeString()}
                    {msg.sentToLearner && <span style={{ marginLeft: 6, color: '#22c55e' }}>Sent</span>}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>{msg.content}</div>
              </div>
            ))
          )}
        </div>

        {/* Reply / Resolve Actions */}
        {!isResolved && (
          <div style={s.replyArea}>
            {showResolve ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea
                  placeholder="Type the answer to send to the learner via WhatsApp..."
                  value={resolveAnswer}
                  onChange={e => setResolveAnswer(e.target.value)}
                  style={{ ...s.textarea, minHeight: 80 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={resolveTicket} disabled={loading} style={{ ...s.btn, background: '#22c55e' }}>
                    {loading ? 'Sending...' : 'Resolve & Send to Learner'}
                  </button>
                  <button onClick={() => setShowResolve(false)} style={{ ...s.btn, background: '#334155' }}>Cancel</button>
                </div>
              </div>
            ) : showNote ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea
                  placeholder="Internal note (not sent to learner)..."
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  style={{ ...s.textarea, borderColor: '#facc1540' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={postNote} disabled={loading} style={{ ...s.btn, background: '#b45309' }}>
                    {loading ? 'Saving...' : 'Add Note'}
                  </button>
                  <button onClick={() => setShowNote(false)} style={{ ...s.btn, background: '#334155' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <textarea
                  placeholder="Type a reply (updates ticket thread only)..."
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  style={s.textarea}
                  onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) postReply(); }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={postReply} disabled={loading || !reply.trim()} style={s.btn}>
                    {loading ? 'Sending...' : 'Add Reply'}
                  </button>
                  <button onClick={() => setShowResolve(true)} style={{ ...s.btn, background: '#22c55e' }}>
                    Resolve & Send Answer
                  </button>
                  <button onClick={() => setShowNote(true)} style={{ ...s.btn, background: '#78350f' }}>
                    Internal Note
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {isResolved && ticket.resolvedAnswer && (
          <div style={{ padding: '12px 16px', background: '#052e1620', borderTop: '1px solid #334155' }}>
            <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 700, marginBottom: 4 }}>Resolution</div>
            <div style={{ fontSize: 13, color: '#86efac' }}>{ticket.resolvedAnswer}</div>
            {ticket.answerSentToLearner && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Sent to learner via WhatsApp</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SupportTickets() {
  const { socket } = useSocket();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ byStatus: {}, byPriority: {} });
  const [filter, setFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSuper = user?.role === 'superadmin';

  useEffect(() => {
    if (isSuper) {
      api.get('/api/org/organizations').then(({ data }) => setOrgs(data)).catch(() => {});
    }
  }, [isSuper]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (filter) params.append('status', filter);
      if (orgFilter) params.append('organizationId', orgFilter);

      const [{ data: ticketData }, { data: statsData }] = await Promise.all([
        api.get(`/api/tickets?${params}`),
        api.get(`/api/tickets/stats?${orgFilter ? `organizationId=${orgFilter}` : ''}`),
      ]);
      setTickets(ticketData.tickets);
      setTotalPages(ticketData.pages);
      setTotal(ticketData.total);
      setStats(statsData);
    } catch {
      setTickets([]);
    }
    setLoading(false);
  }, [filter, orgFilter, page]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  // Real-time updates via socket
  useEffect(() => {
    if (!socket) return;
    const handleCreated = (payload) => {
      // Only reload if we are not filtering by org, or if the new ticket is for our filtered org
      if (!orgFilter || payload.organizationId === orgFilter) {
        loadTickets();
      }
    };
    const handleUpdated = (payload) => {
      setTickets(prev => prev.map(t =>
        t._id === payload.ticketId ? { ...t, ...payload } : t
      ));
      if (selected?._id === payload.ticketId) {
        setSelected(prev => ({ ...prev, ...payload }));
      }
    };
    socket.on('ticket_created', handleCreated);
    socket.on('ticket_updated', handleUpdated);
    return () => {
      socket.off('ticket_created', handleCreated);
      socket.off('ticket_updated', handleUpdated);
    };
  }, [socket, selected, loadTickets, orgFilter]);

  function handleTicketUpdate(updatedTicket) {
    setTickets(prev => prev.map(t => t._id === updatedTicket._id ? { ...t, ...updatedTicket } : t));
    setSelected(updatedTicket);
  }

  const statCards = [
    { label: 'Open',        value: stats.byStatus?.open || 0,        color: STATUS_COLORS.open },
    { label: 'In Progress', value: stats.byStatus?.in_progress || 0, color: STATUS_COLORS.in_progress },
    { label: 'Resolved',    value: stats.byStatus?.resolved || 0,    color: STATUS_COLORS.resolved },
    { label: 'Closed',      value: stats.byStatus?.closed || 0,      color: STATUS_COLORS.closed },
  ];

  return (
    <div style={s.page}>
      <div style={s.headerRow}>
        <div>
          <h1 style={s.title}>Support Tickets</h1>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {total} total {orgFilter && `for ${orgs.find(o => o._id === orgFilter)?.name}`}
          </div>
        </div>

        {isSuper && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontSize: 13, color: '#94a3b8' }}>Organization:</label>
            <select
              value={orgFilter}
              onChange={e => { setOrgFilter(e.target.value); setPage(1); }}
              style={{ ...s.select, width: 200, height: 36 }}
            >
              <option value="">All Organizations</option>
              {orgs.map(o => (
                <option key={o._id} value={o._id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={s.statRow}>
        {statCards.map(card => (
          <div key={card.label} style={{ ...s.statCard, borderColor: card.color + '40' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={s.filterRow}>
        <button
          style={{ ...s.filterBtn, ...(filter === '' ? s.filterActive : {}) }}
          onClick={() => { setFilter(''); setPage(1); }}
        >
          All
        </button>
        {ALL_STATUSES.map(st => (
          <button
            key={st}
            style={{
              ...s.filterBtn,
              ...(filter === st ? { background: STATUS_COLORS[st] + '22', color: STATUS_COLORS[st], borderColor: STATUS_COLORS[st] + '60' } : {}),
            }}
            onClick={() => { setFilter(st); setPage(1); }}
          >
            {STATUS_LABELS[st]}
          </button>
        ))}
      </div>

      {/* Ticket List */}
      {loading ? (
        <div style={{ color: '#64748b', padding: 40, textAlign: 'center' }}>Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 32 }}>🎫</div>
          <div style={{ color: '#64748b', marginTop: 8 }}>No tickets {filter ? `with status "${filter}"` : 'found'}</div>
        </div>
      ) : (
        <div style={s.ticketList}>
          {tickets.map(ticket => (
            <div
              key={ticket._id}
              style={s.ticketRow}
              onClick={() => setSelected(ticket)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 0 }}>
                <div>
                  <span style={{ ...s.badge, background: PRIORITY_COLORS[ticket.priority] + '22', color: PRIORITY_COLORS[ticket.priority] }}>
                    {ticket.priority}
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#64748b' }}>#{ticket.ticketNumber}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ticket.subject}
                    </span>
                    {isSuper && ticket.organizationId?.name && (
                      <span style={{ ...s.badge, background: '#1e293b', color: '#64748b', border: '1px solid #334155' }}>
                        {ticket.organizationId.name}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {ticket.learnerName && <span style={{ color: '#94a3b8' }}>{ticket.learnerName} &middot; </span>}
                    {formatPhone(ticket.phone)}
                    {ticket.assignedTo && (
                      <span style={{ marginLeft: 8, color: '#3b82f6' }}>
                        Assigned: {ticket.assignedTo.name || ticket.assignedTo.email}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                <span style={{ ...s.badge, background: STATUS_COLORS[ticket.status] + '22', color: STATUS_COLORS[ticket.status] }}>
                  {STATUS_LABELS[ticket.status]}
                </span>
                <span style={{ fontSize: 11, color: '#475569' }}>
                  {new Date(ticket.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={s.pagination}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={s.pageBtn}>Prev</button>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={s.pageBtn}>Next</button>
        </div>
      )}

      {/* Detail Drawer */}
      {selected && (
        <TicketDrawer
          ticket={selected}
          onClose={() => setSelected(null)}
          onUpdate={handleTicketUpdate}
        />
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page:       { flex: 1, padding: '24px 28px', overflowY: 'auto', background: '#0f172a', color: '#f1f5f9' },
  headerRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  title:      { fontSize: 22, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  statRow:    { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  statCard:   { flex: 1, minWidth: 120, background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '14px 16px' },
  filterRow:  { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  filterBtn:  { padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: '#1e293b', border: '1px solid #334155', color: '#94a3b8' },
  filterActive: { background: '#334155', color: '#f1f5f9', borderColor: '#64748b' },
  ticketList: { display: 'flex', flexDirection: 'column', gap: 8 },
  ticketRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'border-color 0.15s' },
  badge:      { fontSize: 11, fontWeight: 700, borderRadius: 4, padding: '2px 7px', textTransform: 'uppercase', letterSpacing: 0.5 },
  empty:      { textAlign: 'center', padding: '60px 0', color: '#475569' },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 20 },
  pageBtn:    { padding: '6px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#1e293b', border: '1px solid #334155', color: '#94a3b8' },
  // Drawer
  drawerOverlay: { position: 'fixed', inset: 0, background: '#00000080', zIndex: 100, display: 'flex', justifyContent: 'flex-end' },
  drawer:        { width: '100%', maxWidth: 620, background: '#0f172a', borderLeft: '1px solid #334155', display: 'flex', flexDirection: 'column', overflowY: 'hidden' },
  drawerHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 20px', borderBottom: '1px solid #334155', gap: 12 },
  closeBtn:      { background: 'transparent', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 4 },
  controlRow:    { display: 'flex', gap: 12, padding: '12px 20px', borderBottom: '1px solid #334155', flexWrap: 'wrap' },
  controlLabel:  { display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 700, textTransform: 'uppercase' },
  select:        { background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: '6px 10px', fontSize: 13, width: '100%', cursor: 'pointer' },
  messageThread: { flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 },
  messageItem:   { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', maxWidth: '90%' },
  internalNote:  { background: '#1a1400', borderColor: '#facc1530' },
  replyArea:     { padding: '12px 20px', borderTop: '1px solid #334155' },
  textarea:      { width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', padding: '10px 12px', fontSize: 13, resize: 'vertical', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit' },
  btn:           { padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#3b82f6', border: 'none', color: '#fff' },
};
